import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import AuthFrame from '@/components/auth/AuthFrame';
import { useToast } from '@/components/ToastProvider';
import useAuth from '@/hooks/useAuth';
import {
  confirmSignUp,
  login,
  resendSignUpCode,
  saveIdToken,
  toAuthApiError,
} from '@/services/authApi';
import { URL } from '@/types';
import { takePendingPassword } from '@/utils/pendingPassword';

type ConfirmForm = { email: string; code: string };
/**
 * SignUp（email）または Login の user_not_confirmed（email + resend）から渡される。
 * router state（history.state）はリロード・戻る/進むでも残る（ブラウザがセッション履歴として保持するため）。
 * 直接 URL を開いた場合や新しいタブでは無い
 */
type ConfirmState = { email?: string; resend?: boolean } | null;

export default function SignUpConfirm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const location = useLocation() as { pathname: string; state: ConfirmState };
  const { state } = location;
  const { validationRules, errorMessage } = useAuth();
  const knownEmail = state?.email;
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const autoResent = useRef(false);
  const { control, handleSubmit, getValues, trigger } = useForm<ConfirmForm>({
    defaultValues: { email: knownEmail ?? '', code: '' },
    mode: 'onChange',
  });

  const resend = async (email: string) => {
    if (resending) {
      return;
    }
    setResending(true);
    setError(undefined);
    try {
      await resendSignUpCode(email);
      toast({ message: t('pages:auth:messages:code_resent') });
    } catch (e) {
      setError(errorMessage(toAuthApiError(e).code));
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    // ログイン画面で「メール未確認」だった場合は、ここへ来た時点でコードを 1 回だけ再送する（spec §3.7）。
    // StrictMode（開発時）は effect を 2 回走らせるので ref で 1 回に抑える。
    // 再送を起こした直後に resend フラグを履歴から消す: router state はリロード・戻る/進むでも残るため、
    // 消さないとリロードのたびに再送してしまい、いずれ 429 になる
    if (state?.resend && knownEmail && !autoResent.current) {
      autoResent.current = true;
      resend(knownEmail);
      navigate(location.pathname, { replace: true, state: { email: knownEmail } });
    }
  }, []);

  const onSubmit = async ({ email, code }: ConfirmForm) => {
    setError(undefined);
    setSubmitting(true);
    try {
      await confirmSignUp(email, code);
    } catch (e) {
      setError(errorMessage(toAuthApiError(e).code));
      setSubmitting(false);
      return;
    }

    // 自動ログイン（spec §3.3）。パスワードはメモリ保持分。無ければ（リロード後）ログイン画面へ
    const password = takePendingPassword(email);
    if (password) {
      try {
        const { id_token } = await login(email, password);
        saveIdToken(id_token);
        navigate('/', { replace: true });
        return;
      } catch {
        // 自動ログインに失敗したらログイン画面へ送る
      }
    }
    toast({ message: t('pages:auth:sign_up_confirm:registered_login') });
    navigate(URL.LOGIN, { replace: true, state: { email } });
  };

  const onResendClick = async () => {
    // state 無し画面ではメール欄の入力チェックを通してから再送する（不正なメールで API を叩かない）
    if (!(await trigger('email'))) {
      return;
    }
    resend(getValues('email'));
  };

  return (
    <AuthFrame title={t('pages:auth:sign_up_confirm:title')} error={error}>
      <Typography sx={{ mb: 3 }}>
        {knownEmail
          ? t('pages:auth:sign_up_confirm:lead', { email: knownEmail })
          : t('pages:auth:sign_up_confirm:lead_no_email')}
      </Typography>
      <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3} sx={{ mb: 4 }}>
          {!knownEmail && (
            <Controller
              name="email"
              control={control}
              rules={validationRules.email}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="email"
                  autoComplete="email"
                  label={t('pages:auth:labels:email')}
                  error={!!fieldState.error?.message}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
          <Controller
            name="code"
            control={control}
            rules={validationRules.code}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                inputProps={{ inputMode: 'numeric' }}
                autoComplete="one-time-code"
                label={t('pages:auth:labels:code')}
                error={!!fieldState.error?.message}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Button type="submit" color="primary" variant="contained" disabled={submitting}>
            {t('pages:auth:sign_up_confirm:submit')}
          </Button>
        </Box>
        <Stack alignItems="center">
          <Button type="button" variant="text" onClick={onResendClick} disabled={resending}>
            {t('pages:auth:sign_up_confirm:resend')}
          </Button>
        </Stack>
      </Box>
    </AuthFrame>
  );
}
